/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RangePickerProps } from 'antd/es/date-picker';
import moment from 'moment/moment';
import { RootState } from 'app/AppStore';
import { getPromotionsForTodayEnabled } from 'domains/appContext/AppContext.store';
import {
  findStatusTypeByPromotionID,
  getDuplicatePromotions,
  hasCreatePromotionAnyProductsFilled,
  validateProductsRequestSaga,
} from 'domains/promotion/Promotion.store';
import { CreatePromotionStepWithNavigationProps } from 'domains/promotion/Promotion.types';
import { DuplicatePromotionsDisplay } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PromotionDetailsStep/DuplicatePromotionsDisplay';
import { isFilled } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/StepsValidationUtils/StepsValidationUtils';
import { DatePicker, Flex, MarginBox, Text } from 'UI';
import { hasData } from 'utils';
import {
  addDayToMoment,
  getMomentDate,
  getStringDateFromMoment,
  getStringMaxDate,
  getStringMinDate,
  subtractDayFromMoment,
} from 'utils/date';

const DatesSection = ({
  promotion,
  setPromotion,
  setCurrentStep,
  'data-cy': dataCy,
}: CreatePromotionStepWithNavigationProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const promotionsForTodayEnabled = useSelector(getPromotionsForTodayEnabled);
  const promotionStatus = useSelector((state: RootState) =>
    findStatusTypeByPromotionID(state, hasData(promotion) ? promotion.promotionId : undefined),
  );
  const duplicatePromotions = useSelector((state: RootState) =>
    getDuplicatePromotions(state, promotion.duplicatePromotionIds),
  );
  const isStartDateOverlapping = !!duplicatePromotions?.some((dpr) => {
    const duplicatePromotionStart = dpr.publicationStart ? moment(getMomentDate(dpr.publicationStart)) : null;
    const duplicatePromotionEnd = dpr.publicationEnd
      ? moment(subtractDayFromMoment(getMomentDate(dpr.publicationEnd)))
      : null;

    const promotionStart = promotion.publicationStart ? moment(promotion.publicationStart) : null;
    const promotionEnd = promotion.publicationEnd
      ? moment(subtractDayFromMoment(getMomentDate(promotion.publicationEnd)))
      : null;

    if (!promotionStart || !duplicatePromotionStart) return false;

    return (
      promotionStart.isSameOrAfter(duplicatePromotionStart) ||
      (promotionStart.isSameOrAfter(duplicatePromotionStart) &&
        duplicatePromotionEnd &&
        promotionStart.isSameOrBefore(duplicatePromotionEnd)) ||
      (promotionStart.isSameOrBefore(duplicatePromotionStart) &&
        promotionEnd &&
        duplicatePromotionEnd &&
        promotionEnd.isAfter(duplicatePromotionEnd))
    );
  });

  const isEndDateOverlapping = !!duplicatePromotions?.some((dupr) => {
    const duplicatePromotionStart = dupr.publicationStart
      ? moment(getMomentDate(dupr.publicationStart))
      : moment(getStringMinDate());
    const duplicatePromotionEnd = dupr.publicationEnd
      ? moment(subtractDayFromMoment(getMomentDate(dupr.publicationEnd)))
      : moment(getStringMaxDate());

    const promotionStart = promotion.publicationStart ? moment(promotion.publicationStart) : moment(getStringMinDate());
    const promotionEnd = promotion.publicationEnd
      ? moment(subtractDayFromMoment(getMomentDate(promotion.publicationEnd)))
      : moment(getStringMaxDate());

    return (
      promotionEnd.isSameOrBefore(duplicatePromotionEnd) ||
      (promotionEnd.isSameOrAfter(duplicatePromotionEnd) && promotionStart.isSameOrBefore(duplicatePromotionStart))
    );
  });

  const disabledStartDate: RangePickerProps['disabledDate'] = (current) => {
    const momentToCompare =
      promotionsForTodayEnabled || promotion.promotionType === 'BANNER' ? moment().subtract(1, 'd') : moment();
    return current && current < momentToCompare.endOf('day');
  };
  const disabledEndDate: RangePickerProps['disabledDate'] = (current) => {
    const startDate = moment(promotion.publicationStart);
    if (promotion.promotionType === 'PROMOTION_FLASH_TIME_LIMITED') {
      return (
        current && !(current > startDate.subtract(1, 'd').endOf('day') && current < startDate.add(10, 'd').endOf('day'))
      );
    }
    return current && current < startDate.subtract(1, 'd').endOf('day');
  };

  const renderPublicationDate = () => {
    return (
      <div data-cy={dataCy}>
        <MarginBox mt={45} />
        <Text type="heading3">{t('backoffice.publication_date', 'Publication date')}</Text>
        <Text type={'text'}>
          {t(
            'backoffice.promotion.publication_date.description',
            'Please enter your desired promotion period, by selecting start and end date.',
          )}
        </Text>
        <MarginBox mt={15} />
        <DuplicatePromotionsDisplay
          promotion={promotion}
          setCurrentStep={setCurrentStep}
          promotionStatus={promotionStatus}
        />
        <Flex>
          <DatePicker
            value={getMomentDate(promotion.publicationStart)}
            disabled={promotion.editing}
            onChange={(newValue) => {
              const endMoment = getMomentDate(promotion.publicationEnd);
              const isEndDateStillValid = endMoment && newValue ? endMoment >= newValue : false;
              const newStart = getStringDateFromMoment(newValue);
              const updatedStart = {
                ...promotion,
                publicationStart: newStart,
                publicationEnd: isEndDateStillValid ? promotion.publicationEnd : undefined,
              };
              setPromotion(updatedStart);
              if (
                newValue &&
                hasCreatePromotionAnyProductsFilled(updatedStart) &&
                (endMoment || promotion.promotionType === 'PROMOTION_FLASH_QUANTITY_LIMITED')
              ) {
                dispatch(validateProductsRequestSaga({ promotion: updatedStart }));
              }
            }}
            displayStyle={isStartDateOverlapping ? 'error' : undefined}
            disabledDate={disabledStartDate}
            placeholder={t('common.date.starting', 'Starting')}
            placement={'topRight'}
            data-cy={'starting-date-input'}
          />
          <MarginBox ml={60}>
            <DatePicker
              disabled={!isFilled(promotion.publicationStart)}
              disabledDate={disabledEndDate}
              value={subtractDayFromMoment(getMomentDate(promotion.publicationEnd))}
              onChange={(newValue) => {
                const updatePromotion = {
                  ...promotion,
                  publicationEnd: getStringDateFromMoment(addDayToMoment(newValue)),
                };
                setPromotion(updatePromotion);
                if (newValue && hasCreatePromotionAnyProductsFilled(updatePromotion)) {
                  dispatch(validateProductsRequestSaga({ promotion: updatePromotion }));
                }
              }}
              placeholder={t('common.date.until', 'Until')}
              displayStyle={isEndDateOverlapping ? 'error' : undefined}
              placement={'topRight'}
              data-cy={'ending-date-input'}
            />
          </MarginBox>
        </Flex>
      </div>
    );
  };

  return <>{renderPublicationDate()}</>;
};

export default DatesSection;
